/**
 * BABA BIG - Rechenkern
 *
 * Reine Funktionen, keine I/O, keine Zufalls- oder Zeitabhängigkeit ausser
 * dem optional übergebenen Bezugsjahr. Aus derselben Vorgangsliste folgt
 * immer dasselbe Ergebnis - das ist die Grundlage dafür, dass eine Änderung
 * an einem Vorgang alle abhängigen Zahlen korrekt aktualisiert.
 *
 * Referenzen (R-n) / Abschnittsnummern beziehen sich auf
 * "Baba Big App Spezifikation v1.0".
 */

import { round2, roundN, formatEuro } from './round.ts';
import type {
  Entry,
  TripEntry,
  RefuelEntry,
  RepairEntry,
  SettlementEntry,
  FixedCostEntry,
  Settings,
  TripResult,
  Verbrauchsintervall,
  VerbrauchQuelle,
  PreisQuelle,
  AusgleichErgebnis,
  InstandhaltungErgebnis,
  ParameterUebersicht,
  JahresStatistik,
  Ergebnis,
  Person,
  WerUnterwegs,
  Mitfahrer,
} from './types.ts';

// ---------------------------------------------------------------------------
// Sortierung und Filterung
// ---------------------------------------------------------------------------

/**
 * Aufsteigend nach (datum, seq, id). Die id als letztes Kriterium sorgt für
 * eine stabile, reproduzierbare Reihenfolge auch bei identischem seq.
 */
export function sortEntries(entries: readonly Entry[]): Entry[] {
  return [...entries].sort((a, b) => {
    if (a.datum !== b.datum) return a.datum < b.datum ? -1 : 1;
    if (a.seq !== b.seq) return a.seq - b.seq;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

const isTrip = (e: Entry): e is TripEntry => e.type === 'trip';
const isRefuel = (e: Entry): e is RefuelEntry => e.type === 'refuel';
const isRepair = (e: Entry): e is RepairEntry => e.type === 'repair';
const isSettlement = (e: Entry): e is SettlementEntry => e.type === 'settlement';
const isFixedCost = (e: Entry): e is FixedCostEntry => e.type === 'fixed_cost';

const jahrVon = (datum: string): number => Number(datum.slice(0, 4));

// ---------------------------------------------------------------------------
// Einzelne Vorgangsfelder auflösen
// ---------------------------------------------------------------------------

/**
 * Gefahrene Kilometer eines Ausflugs.
 * Sind Start und Ende gesetzt, gewinnen sie gegenüber einer separat
 * eingetragenen Streckenlänge (Spezifikation 3.2).
 */
export function kmGefahrenVon(trip: TripEntry): number {
  if (trip.kmStart != null && trip.kmEnde != null) {
    return trip.kmEnde - trip.kmStart;
  }
  return trip.kmGefahren ?? 0;
}

/** Besetzung der Rückfahrt; per Vorbelegung gleich der Hinfahrt. */
export function anzRueckVon(trip: TripEntry): number {
  return trip.gleicheAnzahl ? trip.anzHin : (trip.anzRueck ?? trip.anzHin);
}

/** Ein Ausflug ist gestartet, aber noch nicht gestoppt (R-23). */
export function laeuftNoch(trip: TripEntry): boolean {
  return trip.status === 'laufend';
}

/**
 * Der Zählerstand, den ein Vorgang für die Kette beisteuert.
 *
 * Ein Ausflug steuert seinen Anfangsstand bei - er ist der Moment, in dem das
 * Auto wieder losfährt. Tanken und Reparatur ihren abgelesenen Stand.
 */
export function kmStandVon(e: Entry): number | null {
  if (isTrip(e)) return e.kmStart ?? null;
  if (isRefuel(e)) return e.kmStand;
  if (isRepair(e)) return e.kmStand ?? null;
  return null;
}

/**
 * Der laufende Ausflug, den ein neuer Vorgang abschliessen könnte (R-26).
 *
 * Wer losfährt und das Stoppen vergisst, hat später einen Ausflug ohne Ende -
 * er geht in keine Berechnung ein (R-23) und fehlt still im Saldo. Der
 * nächste Vorgang mit einem Zählerstand weiss aber, wo das Auto stand.
 *
 * Gesucht wird der letzte laufende Ausflug VOR dem neuen Vorgang. Sein
 * Anfangsstand muss unter dem neuen Stand liegen, sonst wäre die Strecke
 * negativ und der Vorschlag Unsinn.
 */
export function offenerAusflugDavor(
  entries: readonly Entry[],
  neu: { id?: string; datum: string; seq: number; kmStand: number | null },
): TripEntry | null {
  if (neu.kmStand == null) return null;

  const davor = sortEntries(
    entries.filter(
      (e) =>
        !e.geloescht &&
        e.id !== neu.id &&
        (e.datum < neu.datum || (e.datum === neu.datum && e.seq <= neu.seq)),
    ),
  );

  /*
    Rückwärts bis zum letzten Vorgang, der überhaupt einen Zählerstand hat.
    Nur wenn das der laufende Ausflug selbst ist, steht das Auto seither
    still. Lag dazwischen etwas anderes mit einem Stand - ein abgeschlossener
    Ausflug etwa -, ist das Auto in der Zwischenzeit gefahren, und der neue
    Stand wäre für den laufenden Ausflug viel zu hoch. Vorgänge ohne Stand
    (ein Ausgleich zum Beispiel) werden übergangen.
  */
  for (let i = davor.length - 1; i >= 0; i--) {
    const e = davor[i]!;
    if (kmStandVon(e) == null) continue;
    if (!isTrip(e) || !laeuftNoch(e)) return null;
    if (e.kmStart == null || e.kmStart >= neu.kmStand) return null;
    return e;
  }
  return null;
}

/**
 * Der Zählerstand aus dem, was die Bilderkennung gelesen hat (R-27).
 *
 * Auf dem Display stehen zwei Zahlen übereinander: oben der Tageszähler
 * (kurz, oft mit Komma), unten der Gesamtstand mit sechs Stellen. Nur der
 * untere zählt. Die Bilderkennung liefert Text, keine Koordinaten - deshalb
 * entscheidet die Stellenzahl, nicht die Position: sechs Stellen ist der
 * Gesamtstand, alles andere nicht.
 *
 * Bewusst streng. Ein falsch gelesener Zählerstand verschiebt die Strecke
 * eines Ausflugs und damit echtes Geld; lieber nichts vorschlagen als etwas
 * Falsches, das jemand ungeprüft stehen lässt.
 */
export function zaehlerstandAusText(text: string): number | null {
  /*
    Zahlen einsammeln, Tausenderpunkte und Leerzeichen darin zulassen
    (185.300, 185 300). Eine Zahl mit Komma ist der Tageszähler - sie fliegt
    mitsamt ihrer Nachkommastellen raus.
  */
  const gefunden: number[] = [];
  /*
    Ziffernfolgen mit höchstens einem Trennzeichen dazwischen: 185300,
    185.300 und 185 300 sind dieselbe Zahl. Ein Zeilenumbruch trennt
    dagegen - sonst verschmölzen die beiden Zahlen des Displays zu einer.
  */
  for (const t of text.matchAll(/\d(?:[ .,']?\d)*/g)) {
    const roh = t[0]!;
    // Ein bis zwei Nachkommastellen: der Tageszähler, nicht der Gesamtstand.
    if (/[.,]\d{1,2}$/.test(roh)) continue;
    const ziffern = roh.replace(/\D/g, '');
    if (ziffern.length === 6) gefunden.push(Number(ziffern));
  }

  if (gefunden.length === 0) return null;
  /*
    Mehrere sechsstellige Zahlen kommen vor, wenn die Erkennung den
    Tageszähler mitzählt oder sich vertut. Dann ist keine davon sicher die
    richtige - lieber von Hand eintippen.
  */
  const einmalig = [...new Set(gefunden)];
  return einmalig.length === 1 ? einmalig[0]! : null;
}

/** Von einem Mitfahrer direkt bezahlte Parkgebühr (R-22). */
export function parkVonMitfahrer(trip: TripEntry): number {
  return trip.parkgebuehrVon === 'mitfahrer' ? (trip.parkgebuehr ?? 0) : 0;
}

/**
 * Wie viele Plätze Michi und Otto selbst belegen (R-3).
 * Bei 'keiner' sitzt niemand von beiden im Auto (R-22).
 */
export function eigenePlaetze(werUnterwegs: WerUnterwegs): number {
  if (werUnterwegs === 'beide') return 2;
  if (werUnterwegs === 'keiner') return 0;
  return 1;
}

/**
 * Besetzung, wie sie sich aus der Mitfahrerliste ergibt (R-24).
 *
 * Einzelfahrten zählen ALLE zur Hinfahrt - unabhängig davon, welche Strecke
 * angehakt ist. Das ist bewusst nicht die wörtliche Besetzung, sondern die
 * einzige Lesart, die R-21 richtig rechnet: die Formel liest min(hin, rueck)
 * als Vollmitfahrer und die Differenz als Einzelfahrten. Fährt eine Person
 * nur hin und eine andere nur zurück, sähe die wörtliche Zählung nach zwei
 * Vollmitfahrern aus, und bei 100 EUR Ausflugspreis kämen 110 EUR zusammen.
 *
 * Michi und Otto gelten als hin und zurück mitgefahren.
 *
 * Diese Ableitung ist eine Vorbelegung, keine Wahrheit: sobald jemand die
 * Anzahl von Hand ändert (anzManuell), gilt die eingetippte Zahl.
 */
export function besetzungAusListe(
  werUnterwegs: WerUnterwegs,
  mitfahrer: readonly Mitfahrer[],
): { anzHin: number; anzRueck: number; gleicheAnzahl: boolean } {
  const eigene = eigenePlaetze(werUnterwegs);
  const beide = mitfahrer.filter((m) => m.hin && m.rueck).length;
  const eine = mitfahrer.filter((m) => m.hin !== m.rueck).length;
  return {
    anzHin: eigene + beide + eine,
    anzRueck: eigene + beide,
    gleicheAnzahl: eine === 0,
  };
}

/** Literpreis eines Tankvorgangs (R-4). */
export function preisProLiterVon(refuel: RefuelEntry): number {
  return refuel.liter > 0 ? refuel.betrag / refuel.liter : 0;
}

// ---------------------------------------------------------------------------
// Phase 2: Verbrauchsreihe (R-6)
// ---------------------------------------------------------------------------

/** Kennung des Startwerts, wenn er ein Intervall eröffnet (R-25). */
export const START_ALS_VOLLTANK = 'startwert';

/**
 * Verbrauchsintervalle entstehen ausschliesslich zwischen zwei Volltankungen.
 *
 * Teilbetankungen dazwischen erzeugen keinen eigenen Wert, ihre Liter zählen
 * aber zum umschliessenden Intervall - sonst käme bei einer Teilbetankung ein
 * viel zu niedriger und danach ein viel zu hoher Verbrauch heraus.
 *
 * Der Startwert (R-16) zählt als Volltankung mit null Litern, sofern der Tank
 * damals voll war (R-25, settings.initTankVoll). Ohne ihn hätte die erste
 * Strecke der Aufzeichnung nie einen Verbrauch - der erste Volltank fände
 * keinen Vorgänger.
 */
export function buildVerbrauchsintervalle(
  sortiert: readonly Entry[],
  settings?: Settings,
): Verbrauchsintervall[] {
  const refuels: { r: RefuelEntry; pos: number }[] = [];
  sortiert.forEach((e, pos) => {
    if (isRefuel(e)) refuels.push({ r: e, pos });
  });

  /*
    Der Startwert als gedachter Tankvorgang: kein Sprit, aber ein voller Tank
    an einem bekannten Zählerstand. Position -1, damit er vor allem liegt,
    was danach kommt.
  */
  const start: { r: RefuelEntry; pos: number } | null =
    settings?.initTankVoll === true
      ? {
          pos: -1,
          r: {
            id: START_ALS_VOLLTANK,
            type: 'refuel',
            datum: settings.initDatum,
            seq: -1,
            kmStand: settings.initKmStand,
            liter: 0,
            volltankung: true,
          } as RefuelEntry,
        }
      : null;

  const alle = start ? [start, ...refuels] : refuels;

  const vollIdx = alle
    .map((x, i) => (x.r.volltankung ? i : -1))
    .filter((i) => i >= 0);

  const intervalle: Verbrauchsintervall[] = [];

  for (let k = 0; k + 1 < vollIdx.length; k++) {
    const a = vollIdx[k]!;
    const b = vollIdx[k + 1]!;
    const von = alle[a]!;
    const bis = alle[b]!;

    // Alle Liter NACH dem früheren Volltank bis EINSCHLIESSLICH dem späteren.
    let liter = 0;
    for (let i = a + 1; i <= b; i++) liter += alle[i]!.r.liter;

    const km = bis.r.kmStand - von.r.kmStand;
    if (km <= 0 || liter <= 0) continue; // unplausibel, wird übersprungen

    intervalle.push({
      vonRefuelId: von.r.id,
      bisRefuelId: bis.r.id,
      vonKmStand: von.r.kmStand,
      bisKmStand: bis.r.kmStand,
      liter,
      km,
      verbrauchL100km: (liter / km) * 100,
      posVon: von.pos,
      posBis: bis.pos,
    });
  }

  return intervalle;
}

// ---------------------------------------------------------------------------
// Phase 4: Parameter je Ausflug (Spezifikation 4.1)
// ---------------------------------------------------------------------------

export function resolveVerbrauch(
  posTrip: number,
  intervalle: readonly Verbrauchsintervall[],
  settings: Settings,
): { wert: number; quelle: VerbrauchQuelle; intervall?: Verbrauchsintervall } {
  // V1 - der Ausflug liegt in einem bereits abgeschlossenen Voll-zu-Voll-Intervall.
  const umschliessend = intervalle.find(
    (iv) => iv.posVon < posTrip && posTrip < iv.posBis,
  );
  if (umschliessend) {
    return {
      wert: umschliessend.verbrauchL100km,
      quelle: 'V1',
      intervall: umschliessend,
    };
  }

  // V2 - höchster Verbrauch der letzten drei abgeschlossenen Intervalle davor.
  const davor = intervalle.filter((iv) => iv.posBis < posTrip).slice(-3);
  if (davor.length > 0) {
    return {
      wert: Math.max(...davor.map((iv) => iv.verbrauchL100km)),
      quelle: 'V2',
    };
  }

  // V3 - Startwert (R-5).
  return { wert: settings.startVerbrauchL100km, quelle: 'V3' };
}

/**
 * P1 - höchster Literpreis der letzten drei Volltankungen (R-8).
 *
 * Zwei Einschränkungen, die zusammengehören:
 *
 * Erstens zählen nur Volltankungen, wie beim Verbrauch auch. Sonst stünden auf
 * der Startseite drei Tankvorgänge, von denen nur manche in die Rechnung
 * eingehen - eine Übersicht, die man nicht nachrechnen kann.
 *
 * Zweitens endet das Fenster am selben Zeitpunkt wie beim Verbrauch:
 * Schliesst ein späterer Volltank das Intervall des Ausflugs ab (Regel V1),
 * zählt er mit. Sonst reicht das Fenster nur bis zum Ausflug.
 */
export function resolvePreisProLiter(
  grenzePos: number,
  sortiert: readonly Entry[],
  settings: Settings,
): { wert: number; quelle: PreisQuelle } {
  const bisher: number[] = [];
  for (let i = 0; i <= grenzePos && i < sortiert.length; i++) {
    const e = sortiert[i]!;
    if (isRefuel(e) && e.volltankung) bisher.push(preisProLiterVon(e));
  }
  const letzteDrei = bisher.slice(-3);
  if (letzteDrei.length > 0) {
    return { wert: Math.max(...letzteDrei), quelle: 'P1' };
  }
  return { wert: settings.startPreisProLiter, quelle: 'P2' };
}

// ---------------------------------------------------------------------------
// Phase 4: Ausflug rechnen (Spezifikation 4.2 und 4.3)
// ---------------------------------------------------------------------------

/**
 * Ergebnis eines laufenden Ausflugs: überall null.
 *
 * Er taucht in der Historie auf und lässt sich öffnen, wirkt aber weder auf
 * den Saldo noch auf das Instandhaltungskonto, bis er gestoppt ist.
 */
function leeresErgebnis(
  trip: TripEntry,
  anzHin: number,
  anzRueck: number,
): TripResult {
  return {
    tripId: trip.id,
    kmGefahren: 0,
    anzHin,
    anzRueck,
    verbrauchL100km: 0,
    verbrauchQuelle: 'V3',
    preisProLiter: 0,
    preisQuelle: 'P2',
    treibstoffProKm: 0,
    zusatzkostenProKm: trip.zusatzkostenProKm,
    gesamtProKm: 0,
    anteilEinzelstrecke: trip.anteilEinzelstrecke,
    laeuft: true,
    fahrtkosten: 0,
    parkgebuehr: round2(trip.parkgebuehr ?? 0),
    parkgebuehrVonMitfahrer: 0,
    beitragZahlenderMitfahrer: null,
    preisAusflug: 0,
    preisEineStrecke: 0,
    preisPersonHin: 0,
    preisPersonRueck: 0,
    preisPersonGesamt: 0,
    instandhaltungAusflug: 0,
    instandhaltungPerson: 0,
    nMichi: 0,
    nOtto: 0,
    iMichi: 0,
    iOtto: 0,
    pMichi: 0,
    pOtto: 0,
    ausSnapshot: false,
  };
}

export function computeTrip(
  trip: TripEntry,
  posTrip: number,
  sortiert: readonly Entry[],
  intervalle: readonly Verbrauchsintervall[],
  settings: Settings,
): TripResult {
  // Eingefrorene Ausflüge behalten ihr Ergebnis unverändert (R-7).
  if (trip.finalisiert && trip.snapshot) {
    const s = trip.snapshot;
    return {
      ...s,
      // Snapshots aus der Zeit vor R-21 kennen diese beiden Felder nicht.
      // Damals hing der Preis einer Einzelstrecke an der Besetzung genau
      // dieser Strecke; der Hinfahrtwert ist der bestmögliche Ersatz.
      preisEineStrecke: s.preisEineStrecke ?? s.preisPersonHin,
      anteilEinzelstrecke: s.anteilEinzelstrecke ?? 0.5,
      // Snapshots aus der Zeit vor R-22 und R-23 kennen diese Felder nicht.
      laeuft: false,
      fahrtkosten: s.fahrtkosten ?? s.preisAusflug,
      parkgebuehr: s.parkgebuehr ?? 0,
      parkgebuehrVonMitfahrer: s.parkgebuehrVonMitfahrer ?? 0,
      beitragZahlenderMitfahrer: s.beitragZahlenderMitfahrer ?? null,
      pMichi: s.pMichi ?? 0,
      pOtto: s.pOtto ?? 0,
      ausSnapshot: true,
    };
  }

  const anzHin = trip.anzHin;
  const anzRueck = anzRueckVon(trip);

  /*
    Ein laufender Ausflug geht in keine Berechnung ein (R-23).

    Seine Strecke steht noch nicht fest - jeder Preis wäre geraten, und der
    Saldo auf der Startseite würde sich mit dem Stoppen sprunghaft ändern.
    Bewusst ein eigener Zweig statt sich darauf zu verlassen, dass ohne
    End-Zählerstand ohnehin null herauskommt: ein von Hand eingetragenes
    kmGefahren würde sonst mitten in der Fahrt zu zählen anfangen.
  */
  if (laeuftNoch(trip)) {
    return leeresErgebnis(trip, anzHin, anzRueck);
  }

  const kmGefahren = kmGefahrenVon(trip);

  const v = resolveVerbrauch(posTrip, intervalle, settings);
  // Bis wohin die letzten drei Tankvorgänge gezählt werden: bis zum
  // abschliessenden Volltank, sonst bis unmittelbar vor den Ausflug.
  const grenzePos = v.intervall ? v.intervall.posBis : posTrip - 1;
  const p = resolvePreisProLiter(grenzePos, sortiert, settings);

  // Ein von Hand gesetzter Wert ersetzt erst hier das Ergebnis der Regeln.
  //
  // Bewusst nicht früher: das Fenster für den Literpreis endet dort, wo das
  // Verbrauchsintervall endet (grenzePos). Würde ein manueller Verbrauch die
  // Regelauswertung überspringen, verschöbe sich damit stillschweigend auch
  // das Preisfenster - ein von Hand gesetzter Verbrauch würde den automatisch
  // ermittelten Literpreis verändern. Die beiden Werte bleiben unabhängig.
  const verbrauchL100km = trip.verbrauchL100kmManuell ?? v.wert;
  const verbrauchQuelle: VerbrauchQuelle =
    trip.verbrauchL100kmManuell != null ? 'VM' : v.quelle;
  const preisProLiter = trip.preisProLiterManuell ?? p.wert;
  const preisQuelle: PreisQuelle =
    trip.preisProLiterManuell != null ? 'PM' : p.quelle;

  const treibstoffProKm = (verbrauchL100km / 100) * preisProLiter;
  const gesamtProKm = treibstoffProKm + trip.zusatzkostenProKm;

  /*
    Umlage auf die MitfahrerInnen (R-21).

    Die frühere Fassung teilte jede Strecke durch ihre eigene Besetzung. Das
    führte zu zwei Widersprüchen: Wer nur eine Strecke mitfuhr, zahlte je nach
    Besetzung dieser Strecke einmal mehr, einmal weniger als die Hälfte - und
    die beiden Einzelstrecken kosteten unterschiedlich viel, obwohl sie gleich
    lang sind.

    Stattdessen wird der Ausflugspreis nach Anteilen umgelegt: Wer beide
    Strecken fährt, zählt 1,0; wer nur eine fährt, zählt anteilEinzelstrecke
    (z. B. 0,65). Über 0,5, weil das Auto die ganze Strecke bewegt werden muss.

    Die Besetzungszahlen werden dabei als Teilmenge gelesen: Bei 7 hin und 5
    zurück fahren 5 Personen beide Strecken und 2 nur eine.

    Entscheidend ist, dass die Summe aller Beiträge der Ausflugspreis bleibt.
    Ein Aufschlag ohne Gegenstück würde bedeuten, dass die kassierende Person
    mehr einnimmt, als sie der Kasse schuldet - sie verdiente am gemeinsamen
    Auto (siehe Spezifikation, Diskussion zu R-1).
  */
  /*
    Fährt die Person, die das Parken ausgelegt hat, nur eine Strecke, zahlt
    sie auch nur deren Anteil (R-21). Der Rechenkern greift dafür als
    einziges Mal in die Liste - der Betrag gehört zum Ergebnis des Ausflugs,
    nicht zur Buchführung darüber. Ohne Namen bleibt es beim vollen Anteil,
    so wie es Ausflüge vor R-24 gespeichert haben.
  */
  const zahler = trip.parkgebuehrMitfahrerId
    ? trip.mitfahrer?.find((m) => m.id === trip.parkgebuehrMitfahrerId)
    : undefined;
  const anteilZahler =
    zahler && !(zahler.hin && zahler.rueck) ? trip.anteilEinzelstrecke : 1;

  const beideStrecken = Math.min(anzHin, anzRueck);
  const nurEineStrecke = Math.abs(anzHin - anzRueck);
  const anteile = beideStrecken + nurEineStrecke * trip.anteilEinzelstrecke;

  // Gerundet wird erst am Schluss, damit sich keine zwei Rundungsfehler
  // aufsummieren (z. B. 2,95625 + 2,95625 -> 2,96 + 2,96 = 5,92 statt 5,91).
  /*
    Die Parkgebühr gehört zum Ausflugspreis und wird nach denselben Anteilen
    umgelegt wie die Fahrt (R-22) - eine Regel statt zwei. Sie ist aber weder
    Treibstoff noch Instandhaltung, geht also nicht in gesamtProKm ein und
    erhöht das Instandhaltungskonto nicht.
  */
  const fahrtkostenExakt = kmGefahren * gesamtProKm;
  const park = trip.parkgebuehr ?? 0;
  const preisAusflugExakt = fahrtkostenExakt + park;
  const exaktGesamt = anteile > 0 ? preisAusflugExakt / anteile : 0;

  const fahrtkosten = round2(fahrtkostenExakt);
  const preisAusflug = round2(preisAusflugExakt);
  const preisPersonGesamt = round2(exaktGesamt);
  const preisEineStrecke = round2(trip.anteilEinzelstrecke * exaktGesamt);
  // Die Richtung entscheidet nicht mehr über den Preis.
  const preisPersonHin = preisEineStrecke;
  const preisPersonRueck = preisEineStrecke;

  const instandhaltungExakt = kmGefahren * trip.zusatzkostenProKm;
  const instandhaltungAusflug = round2(instandhaltungExakt);
  const instandhaltungPerson = round2(
    anteile > 0 ? instandhaltungExakt / anteile : 0,
  );

  /*
    Geld, das ein Mitfahrer direkt an der Schranke gelassen hat, ist nie durch
    die Hand des Kassierers gegangen - er schuldet es der Kasse also auch
    nicht. Der Mitfahrer legt beim Kassieren entsprechend weniger hin.
  */
  const parkExtern = parkVonMitfahrer(trip);
  const kassenbetragExakt = preisAusflugExakt - parkExtern;
  const kassenbetrag = round2(kassenbetragExakt);

  const verteilung = verteileAufKasse(
    trip,
    kassenbetragExakt,
    exaktGesamt,
    kassenbetrag,
    instandhaltungExakt,
    anteile > 0 ? instandhaltungExakt / anteile : 0,
    instandhaltungAusflug,
    park - parkExtern,
    anteile > 0 ? park / anteile : 0,
    round2(park - parkExtern),
  );

  return {
    tripId: trip.id,
    kmGefahren,
    anzHin,
    anzRueck,
    verbrauchL100km,
    verbrauchQuelle,
    preisProLiter,
    preisQuelle,
    treibstoffProKm,
    zusatzkostenProKm: trip.zusatzkostenProKm,
    gesamtProKm,
    anteilEinzelstrecke: trip.anteilEinzelstrecke,
    laeuft: false,
    fahrtkosten,
    parkgebuehr: round2(park),
    parkgebuehrVonMitfahrer: round2(parkExtern),
    /*
      Was die auslegende Person am Ende noch hinlegt; negativ heisst
      "bekommt zurück". Ihr eigener Anteil richtet sich nach ihren Strecken,
      seit sie namentlich in der Liste steht (R-24). Vorher galt pauschal
      "hin und zurück" - bei einer Person, die nur eine Strecke mitfuhr,
      kassierte der Ausflug damit nachweislich zu viel.
    */
    beitragZahlenderMitfahrer:
      parkExtern > 0
        ? round2(anteilZahler * exaktGesamt - parkExtern)
        : null,
    preisAusflug,
    preisEineStrecke,
    preisPersonHin,
    preisPersonRueck,
    preisPersonGesamt,
    instandhaltungAusflug,
    instandhaltungPerson,
    ...verteilung,
    ausSnapshot: false,
  };
}

/**
 * Wer schuldet der Kasse was (R-1 / R-2 / R-3).
 *
 * Fährt nur einer, schuldet er den ganzen Ausflugspreis, weil er die Beiträge
 * der MitfahrerInnen einsammelt. Fahren beide, schuldet der Kassierer den
 * Ausflugspreis abzüglich des Anteils der anderen Person, die ihren eigenen
 * Anteil direkt schuldet. Die Summe ist in jedem Fall der Ausflugspreis.
 */
/**
 * Zwei Beträge runden, ohne dass ihre Summe von der Vorgabe abweicht.
 *
 * Bei ungeradem Cent lässt sich der Rest nicht halbieren. Er geht an den
 * grösseren der beiden Beträge; bei Gleichstand an Otto - das entspricht der
 * Aufteilung, die vorher bei "beide unterwegs ohne Kassierer" herauskam.
 *
 * Entscheidend ist, dass die Regel NICHT davon abhängt, wer kassiert hat.
 * Vorher fiel der Restcent immer dem Nicht-Kassierer zu. Bei einem Ausflug nur
 * zu zweit, wo beide dasselbe schulden, kippte der Saldo dadurch je nach
 * Kassierer zwischen +0,01 und -0,01 EUR - gedeckt von R-15, aber auf der
 * Startseite als "Michi schuldet Otto 0,01 EUR" sichtbar und nicht erklärbar.
 */
function teileOhneRest(
  exaktMichi: number,
  exaktOtto: number,
  summe: number,
): { michi: number; otto: number } {
  let michi = round2(exaktMichi);
  let otto = round2(exaktOtto);

  const rest = round2(summe - round2(michi + otto));
  if (rest !== 0) {
    if (exaktMichi > exaktOtto) michi = round2(michi + rest);
    else otto = round2(otto + rest);
  }
  return { michi, otto };
}

/**
 * Wer schuldet der Kasse was (R-1 / R-2 / R-3).
 *
 * Fährt nur einer, schuldet er den ganzen Ausflugspreis, weil er die Beiträge
 * der MitfahrerInnen einsammelt. Fahren beide, schuldet der Kassierer den
 * Ausflugspreis abzüglich des Anteils der anderen Person, die ihren eigenen
 * Anteil direkt schuldet. Die Summe ist in jedem Fall der Ausflugspreis.
 *
 * Gerechnet wird mit den ungerundeten Anteilen; gerundet wird erst am Schluss
 * in teileOhneRest, damit die Summe stimmt und der Restcent nicht an der
 * Kassiererrolle hängt.
 */
function verteileAufKasse(
  trip: TripEntry,
  preisAusflugExakt: number,
  preisPersonExakt: number,
  preisAusflug: number,
  instandhaltungExakt: number,
  instandhaltungPersonExakt: number,
  instandhaltungAusflug: number,
  parkKassenExakt: number,
  parkPersonExakt: number,
  parkKassen: number,
): {
  nMichi: number; nOtto: number;
  iMichi: number; iOtto: number;
  pMichi: number; pOtto: number;
} {
  /** Ungerundete Anteile (michi, otto) einer Summe nach der Rollenverteilung. */
  function anteileVon(gesamt: number, person: number): [number, number] {
    if (trip.werUnterwegs === 'michi') return [gesamt, 0];
    if (trip.werUnterwegs === 'otto') return [0, gesamt];

    /*
      Niemand von beiden sitzt im Auto (R-22): Keiner hat einen eigenen Platz
      zu zahlen, aber jemand sammelt das Geld ein und schuldet es der Kasse.
      Deshalb trägt der Kassierer alles - ohne Kassierer wäre nicht bestimmbar,
      wer das Geld hat, dann bleibt es beim hälftigen Teilen.
    */
    if (trip.werUnterwegs === 'keiner') {
      if (!trip.kassierer) return [gesamt / 2, gesamt / 2];
      return trip.kassierer === 'michi' ? [gesamt, 0] : [0, gesamt];
    }

    // Beide unterwegs: ohne Angabe des Kassierers wird hälftig geteilt.
    if (!trip.kassierer) return [gesamt / 2, gesamt / 2];

    return trip.kassierer === 'michi'
      ? [gesamt - person, person]
      : [person, gesamt - person];
  }

  const [nExaktMichi, nExaktOtto] = anteileVon(
    preisAusflugExakt,
    preisPersonExakt,
  );
  const [iExaktMichi, iExaktOtto] = anteileVon(
    instandhaltungExakt,
    instandhaltungPersonExakt,
  );

  const [pExaktMichi, pExaktOtto] = anteileVon(parkKassenExakt, parkPersonExakt);

  const n = teileOhneRest(nExaktMichi, nExaktOtto, preisAusflug);
  const i = teileOhneRest(iExaktMichi, iExaktOtto, instandhaltungAusflug);
  const p = teileOhneRest(pExaktMichi, pExaktOtto, parkKassen);

  return {
    nMichi: n.michi, nOtto: n.otto,
    iMichi: i.michi, iOtto: i.otto,
    pMichi: p.michi, pOtto: p.otto,
  };
}

// ---------------------------------------------------------------------------
// Phase 5: Ausgleich (Spezifikation 4.4)
// ---------------------------------------------------------------------------

export function computeAusgleich(
  sortiert: readonly Entry[],
  trips: ReadonlyMap<string, TripResult>,
): AusgleichErgebnis {
  let eMichi = 0;
  let eOtto = 0;
  let aMichi = 0;
  let aOtto = 0;
  let nMichi = 0;
  let nOtto = 0;

  // Für die Zerlegung des Saldos nach Herkunft getrennt mitgezählt.
  let tankMichi = 0;
  let tankOtto = 0;
  let repMichi = 0;
  let repOtto = 0;
  let fixMichi = 0;
  let fixOtto = 0;
  let instMichi = 0;
  let instOtto = 0;
  // Parkgebühren hängen am Ausflug, nicht an einem eigenen Vorgang (R-22).
  let parkAuslageMichi = 0;
  let parkAuslageOtto = 0;
  let parkNutzungMichi = 0;
  let parkNutzungOtto = 0;

  for (const e of sortiert) {
    if (isRefuel(e) || isRepair(e) || isFixedCost(e)) {
      if (e.bezahltVon === 'michi') eMichi += e.betrag;
      else eOtto += e.betrag;

      if (isRefuel(e)) {
        if (e.bezahltVon === 'michi') tankMichi += e.betrag;
        else tankOtto += e.betrag;
      } else if (isRepair(e)) {
        if (e.bezahltVon === 'michi') repMichi += e.betrag;
        else repOtto += e.betrag;
      } else {
        if (e.bezahltVon === 'michi') fixMichi += e.betrag;
        else fixOtto += e.betrag;
      }
    } else if (isSettlement(e)) {
      if (e.richtung === 'michi_an_otto') aMichi += e.betrag;
      else aOtto += e.betrag;
    } else if (isTrip(e)) {
      const r = trips.get(e.id);
      if (r) {
        nMichi += r.nMichi;
        nOtto += r.nOtto;
        instMichi += r.iMichi;
        instOtto += r.iOtto;
        parkNutzungMichi += r.pMichi;
        parkNutzungOtto += r.pOtto;
      }
      // Wer die Parkgebühr ausgelegt hat, hat sie vorgestreckt - wie beim
      // Tanken. Ein zahlender Mitfahrer taucht hier nicht auf: sein Geld
      // mindert bereits die Schuld des Kassierers.
      if (e.parkgebuehrVon === 'michi') parkAuslageMichi += e.parkgebuehr ?? 0;
      else if (e.parkgebuehrVon === 'otto') parkAuslageOtto += e.parkgebuehr ?? 0;
    }
  }

  eMichi += parkAuslageMichi;
  eOtto += parkAuslageOtto;

  // Treibstoffanteil der Nutzungsschuld = Nutzung ohne Instandhaltung und
  // ohne Parken. Ohne diesen Abzug rutschten Parkgebühren in den Topf
  // "Treibstoff" und die Aufschlüsselung wäre falsch beschriftet.
  const treibMichi = nMichi - instMichi - parkNutzungMichi;
  const treibOtto = nOtto - instOtto - parkNutzungOtto;

  // Die Reparaturen laufen gegen die eingezahlten Instandhaltungsbeiträge;
  // ein Fehlbetrag - das Konto darf ins Minus - wird hälftig getragen (R-9).
  const anteilInstandhaltung = 0.5 * (repMichi - repOtto - (instMichi - instOtto));
  const anteilTreibstoff = 0.5 * (tankMichi - tankOtto - (treibMichi - treibOtto));
  const anteilFixkosten = 0.5 * (fixMichi - fixOtto);
  const anteilParkgebuehren =
    0.5 *
    (parkAuslageMichi -
      parkAuslageOtto -
      (parkNutzungMichi - parkNutzungOtto));
  const anteilAusgleichszahlungen = aMichi - aOtto;

  eMichi = round2(eMichi);
  eOtto = round2(eOtto);
  nMichi = round2(nMichi);
  nOtto = round2(nOtto);
  aMichi = round2(aMichi);
  aOtto = round2(aOtto);

  // Aus den ungerundeten Teilen gebildet - so ergibt die Zerlegung exakt den
  // Gesamtsaldo und stimmt zugleich mit der Formel aus Spezifikation 4.4 überein.
  const saldoMichi = round2(
    anteilInstandhaltung +
      anteilTreibstoff +
      anteilFixkosten +
      anteilParkgebuehren +
      anteilAusgleichszahlungen,
  );

  let schuldner: Person | null = null;
  let glaeubiger: Person | null = null;
  let text = 'Alles ausgeglichen';

  if (saldoMichi > 0) {
    schuldner = 'otto';
    glaeubiger = 'michi';
    text = `Otto schuldet Michi: ${formatEuro(saldoMichi)}`;
  } else if (saldoMichi < 0) {
    schuldner = 'michi';
    glaeubiger = 'otto';
    text = `Michi schuldet Otto: ${formatEuro(-saldoMichi)}`;
  }

  return {
    eMichi,
    eOtto,
    nMichi,
    nOtto,
    aMichi,
    aOtto,
    saldoMichi,
    anteilInstandhaltung: round2(anteilInstandhaltung),
    anteilTreibstoff: round2(anteilTreibstoff),
    anteilFixkosten: round2(anteilFixkosten),
    anteilParkgebuehren: round2(anteilParkgebuehren),
    anteilAusgleichszahlungen: round2(anteilAusgleichszahlungen),
    schuldner,
    glaeubiger,
    betrag: Math.abs(saldoMichi),
    text,
  };
}

// ---------------------------------------------------------------------------
// Phase 5: Konto Instandhaltung (Spezifikation 4.5)
// ---------------------------------------------------------------------------

/**
 * Instandhaltungskonto (R-9).
 *
 * Reine Deckungsanzeige: reichen die kassierten Beiträge für die bisherigen
 * Reparaturen? Bewusst ohne Aufteilung nach Person - die gehört in den
 * Ausgleich (computeAusgleich -> anteilInstandhaltung), weil die Beiträge kein
 * Guthaben der Person sind, sondern kassiertes Geld, das sie dem Topf schuldet.
 *
 * Ein negativer Kontostand ist kein Fehler, sondern der Hinweis, dass
 * zusatzkosten_pro_km zu niedrig angesetzt ist.
 */
export function computeInstandhaltung(
  sortiert: readonly Entry[],
  trips: ReadonlyMap<string, TripResult>,
): InstandhaltungErgebnis {
  let einzahlungen = 0;
  let auszahlungen = 0;

  for (const e of sortiert) {
    if (isTrip(e)) {
      const r = trips.get(e.id);
      if (r) einzahlungen += r.iMichi + r.iOtto;
    } else if (isRepair(e)) {
      auszahlungen += e.betrag;
    }
  }

  einzahlungen = round2(einzahlungen);
  auszahlungen = round2(auszahlungen);
  const kontostand = round2(einzahlungen - auszahlungen);
  const ausreichend = kontostand >= 0;

  return {
    einzahlungen,
    auszahlungen,
    kontostand,
    ausreichend,
    text: ausreichend
      ? 'Die Beiträge decken die Reparaturen.'
      : `Die Beiträge decken die Reparaturen nicht: ${formatEuro(-kontostand)} fehlen.`,
  };
}

// ---------------------------------------------------------------------------
// Phase 5: Statistik (Spezifikation 4.6)
// ---------------------------------------------------------------------------

interface Zaehlerstand {
  datum: string;
  km: number;
}

/**
 * Die Zählerstandskette wird allein aus dem Importwert und den Tankvorgängen
 * gebildet (R-10). Ausflüge tragen nichts bei, weil ihr KM-Stand optional ist
 * und die Kette dadurch Lücken bekäme.
 */
export function buildZaehlerkette(
  sortiert: readonly Entry[],
  settings: Settings,
): Zaehlerstand[] {
  const kette: Zaehlerstand[] = [
    { datum: settings.initDatum, km: settings.initKmStand },
  ];
  for (const e of sortiert) {
    if (isRefuel(e)) kette.push({ datum: e.datum, km: e.kmStand });
  }
  return kette.sort((a, b) => (a.datum < b.datum ? -1 : a.datum > b.datum ? 1 : 0));
}

function teile(zaehler: number, nenner: number): number | null {
  return nenner > 0 ? roundN(zaehler / nenner, 4) : null;
}

export function computeStatistik(
  sortiert: readonly Entry[],
  trips: ReadonlyMap<string, TripResult>,
  settings: Settings,
  bisJahr?: number,
): JahresStatistik[] {
  const kette = buildZaehlerkette(sortiert, settings);
  const startJahr = jahrVon(settings.initDatum);
  const datenJahre = sortiert.map((e) => jahrVon(e.datum));
  const endJahr = Math.max(startJahr, bisJahr ?? startJahr, ...datenJahre);

  const ergebnis: JahresStatistik[] = [];

  for (let jahr = startJahr; jahr <= endJahr; jahr++) {
    const jahresBeginn = `${jahr}-01-01`;
    const jahresEnde = `${jahr}-12-31`;

    const letzterImJahr = [...kette]
      .filter((z) => z.datum >= jahresBeginn && z.datum <= jahresEnde)
      .pop();
    const letzterDavor = [...kette].filter((z) => z.datum < jahresBeginn).pop();

    let kmJahr = 0;
    if (letzterImJahr) {
      // Im Startjahr gibt es keinen Stand aus dem Vorjahr; dann bildet der
      // erste Stand des Jahres (der Import) die Basis.
      const basis =
        letzterDavor ??
        [...kette]
          .filter((z) => z.datum >= jahresBeginn && z.datum <= jahresEnde)
          .shift();
      if (basis) kmJahr = roundN(letzterImJahr.km - basis.km, 1);
    } else if (letzterDavor) {
      kmJahr = 0; // in diesem Jahr wurde nicht getankt
    }

    let kostenTreibstoff = 0;
    let kostenReparatur = 0;
    let kostenFix = 0;
    let kostenParken = 0;
    let kmMitMitfahrern = 0;
    let kmOhneMitfahrer = 0;

    for (const e of sortiert) {
      if (jahrVon(e.datum) !== jahr) continue;
      if (isRefuel(e)) kostenTreibstoff += e.betrag;
      else if (isRepair(e)) kostenReparatur += e.betrag;
      else if (isFixedCost(e)) kostenFix += e.betrag;
      else if (isTrip(e)) {
        const r = trips.get(e.id);
        if (!r) continue;
        kostenParken += r.parkgebuehr;
        const kmRichtung = r.kmGefahren / 2;
        // Wie viele Plätze belegen Michi und Otto selbst? Alles darüber sind
        // MitfahrerInnen von aussen.
        const eigene = eigenePlaetze(e.werUnterwegs);
        for (const anzahl of [r.anzHin, r.anzRueck]) {
          if (anzahl > eigene) kmMitMitfahrern += kmRichtung;
          else kmOhneMitfahrer += kmRichtung;
        }
      }
    }

    kostenTreibstoff = round2(kostenTreibstoff);
    kostenReparatur = round2(kostenReparatur);
    kostenFix = round2(kostenFix);
    kostenParken = round2(kostenParken);
    kmMitMitfahrern = roundN(kmMitMitfahrern, 1);
    kmOhneMitfahrer = roundN(kmOhneMitfahrer, 1);

    ergebnis.push({
      jahr,
      kmJahr,
      kostenTreibstoff,
      kostenReparatur,
      kostenFix,
      kostenParken,
      proKmTreibstoff: teile(kostenTreibstoff, kmJahr),
      proKmReparatur: teile(kostenReparatur, kmJahr),
      proKmTreibstoffUndReparatur: teile(
        kostenTreibstoff + kostenReparatur,
        kmJahr,
      ),
      proKmFix: teile(kostenFix, kmJahr),
      proKmGesamt: teile(
        kostenTreibstoff + kostenReparatur + kostenFix,
        kmJahr,
      ),
      kmMitMitfahrern,
      kmOhneMitfahrer,
      // Näherungswert: Kette und Ausflüge stammen aus verschiedenen Quellen.
      kmNichtAlsAusflugErfasst: roundN(
        Math.max(0, kmJahr - kmMitMitfahrern - kmOhneMitfahrer),
        1,
      ),
    });
  }

  return ergebnis;
}

// ---------------------------------------------------------------------------
// Parameterübersicht für die Startseite
// ---------------------------------------------------------------------------

/**
 * Womit ein heute angelegter Ausflug rechnen würde.
 *
 * Zeigt die letzten drei Tankvorgänge mit Literpreis und - sofern der
 * Tankvorgang ein Voll-zu-Voll-Intervall abschliesst - dessen Verbrauch.
 * Markiert ist jeweils der Höchstwert, denn den wählen die Regeln P1 und V2.
 */
export function computeParameter(
  sortiert: readonly Entry[],
  intervalle: readonly Verbrauchsintervall[],
  settings: Settings,
): ParameterUebersicht {
  // Nur Volltankungen: allein sie gehen in Verbrauch und Literpreis ein.
  const refuels = sortiert.filter(isRefuel).filter((r) => r.volltankung).slice(-3);

  const zeilen = refuels.map((r) => {
    const iv = intervalle.find((x) => x.bisRefuelId === r.id);
    return {
      id: r.id,
      datum: r.datum,
      preisProLiter: preisProLiterVon(r),
      verbrauchL100km: iv ? iv.verbrauchL100km : null,
      istHoechsterPreis: false,
      istHoechsterVerbrauch: false,
    };
  });

  const preise = zeilen.map((z) => z.preisProLiter);
  const verbraeuche = zeilen
    .map((z) => z.verbrauchL100km)
    .filter((v): v is number => v != null);

  const hoechsterPreisProLiter =
    preise.length > 0 ? Math.max(...preise) : settings.startPreisProLiter;
  const hoechsterVerbrauchL100km =
    verbraeuche.length > 0
      ? Math.max(...verbraeuche)
      : settings.startVerbrauchL100km;

  // Nur die erste Fundstelle markieren, damit bei gleichen Werten nicht
  // zwei Zeilen fett erscheinen.
  const preisIdx = zeilen.findIndex(
    (z) => z.preisProLiter === hoechsterPreisProLiter,
  );
  if (preisIdx >= 0) zeilen[preisIdx]!.istHoechsterPreis = true;

  const verbrauchIdx = zeilen.findIndex(
    (z) => z.verbrauchL100km === hoechsterVerbrauchL100km,
  );
  if (verbrauchIdx >= 0) zeilen[verbrauchIdx]!.istHoechsterVerbrauch = true;

  return {
    tankvorgaenge: zeilen,
    hoechsterPreisProLiter,
    hoechsterVerbrauchL100km,
    ausStartwerten: preise.length === 0 || verbraeuche.length === 0,
  };
}

// ---------------------------------------------------------------------------
// Einstiegspunkt
// ---------------------------------------------------------------------------

/**
 * Berechnet aus der kompletten Vorgangsliste sämtliche Anzeigewerte.
 * Wird nach jeder Änderung einmal komplett aufgerufen.
 */
export function computeAll(
  entries: readonly Entry[],
  settings: Settings,
  optionen?: { bisJahr?: number },
): Ergebnis {
  const sortiert = sortEntries(entries.filter((e) => !e.geloescht));
  const intervalle = buildVerbrauchsintervalle(sortiert, settings);

  const trips = new Map<string, TripResult>();
  sortiert.forEach((e, pos) => {
    if (isTrip(e)) {
      trips.set(e.id, computeTrip(e, pos, sortiert, intervalle, settings));
    }
  });

  return {
    trips,
    verbrauchsintervalle: intervalle,
    ausgleich: computeAusgleich(sortiert, trips),
    instandhaltung: computeInstandhaltung(sortiert, trips),
    parameter: computeParameter(sortiert, intervalle, settings),
    statistik: computeStatistik(sortiert, trips, settings, optionen?.bisJahr),
  };
}

/**
 * Erzeugt den Snapshot beim Einfrieren eines Ausflugs (R-7).
 * Die App speichert das Ergebnis am Ausflug und ruft danach computeAll erneut.
 */
export function snapshotFuer(
  trip: TripEntry,
  entries: readonly Entry[],
  settings: Settings,
): TripResult {
  const ergebnis = computeAll(entries, settings);
  const r = ergebnis.trips.get(trip.id);
  if (!r) throw new Error(`Ausflug ${trip.id} nicht in der Vorgangsliste`);
  return { ...r, ausSnapshot: false };
}

export { round2, roundN, formatEuro };

// Die erlaubten Werte von photo.feld gehören zum gemeinsamen Wortschatz von
// Formular und Worker - deshalb hier mit hinaus.
export { FOTO_FELDER } from './types.ts';
export type { FotoFeld } from './types.ts';
